import l10n from '../../../shared/strings';
import { resolveChannelLabel } from '../../../store/products/helpers/showPublishedToChannelToast';

const MASTER_LAYER_VALUE = 'master';

export const normalizeProductLayerOptions = (options = [], salesChannels = []) => (
  options.map((option) => {
    if (option === MASTER_LAYER_VALUE) {
      return {
        label: l10n.SEARCH.productLayer.master,
        value: MASTER_LAYER_VALUE,
      };
    }

    return {
      label: resolveChannelLabel(option, salesChannels),
      value: String(option),
    };
  })
);

export const toProductLayerDropdownItems = (options = []) => (
  options.map(({ label, value }) => ({
    title: label,
    value,
    _id: value,
  }))
);

export const restoreProductLayerFilterValue = (layerIds = [], normalizedOptions = []) => {
  const optionByValue = normalizedOptions.reduce((acc, option) => {
    acc[option.value] = option;
    return acc;
  }, {});

  return layerIds.map((layerId) => {
    const value = String(layerId);
    const option = optionByValue[value];

    return {
      title: option?.label || value,
      value,
      _id: value,
    };
  });
};

export const serializeProductLayerForRoute = (value) => {
  if (!Array.isArray(value) || !value.length) return value;

  return value.map((item) => item?.value ?? item?._id ?? item);
};

const layerIdOf = (item) => String(item?._id ?? item?.value ?? item ?? '');

export const isMasterOnlyProductLayer = (value) => {
  if (!Array.isArray(value) || value.length !== 1) return false;
  return layerIdOf(value[0]) === MASTER_LAYER_VALUE;
};

export const hasChannelProductLayer = (value) => (
  Array.isArray(value) && value.some((item) => layerIdOf(item) !== MASTER_LAYER_VALUE)
);

/**
 * Record state applies only after a channel is selected.
 * Returns 'ALL' to set it, null to clear it, or undefined to leave it unchanged.
 */
export const nextListingStatusForLayerChange = (previousLayers, nextLayers, currentStatus) => {
  if (!hasChannelProductLayer(nextLayers)) return null;
  if (!hasChannelProductLayer(previousLayers) || !currentStatus) return 'ALL';
  return undefined;
};

const layerTitleOf = (item) => item?.title || item?.name || item?.label || layerIdOf(item);

const recordStateLabel = (listingStatus, labels) => (
  listingStatus === 'ALL' ? labels.allStates : (labels.statusLabel || listingStatus)
);

export const formatProductLayerChipTitle = (layers, listingStatus, labels = {}) => {
  const items = Array.isArray(layers) ? layers : [];
  if (!items.length) return labels.master || '';

  const first = layerTitleOf(items[0]);
  const extra = items.length - 1;
  const channelWord = extra === 1 ? labels.channelWord : labels.channelsWord;
  const layerPart = extra > 0 ? `${first} +${extra} ${channelWord}` : first;

  if (!hasChannelProductLayer(items)) return layerPart;

  const stateLabel = !listingStatus || listingStatus === 'ALL'
    ? labels.allStates
    : recordStateLabel(listingStatus, labels);

  return `${layerPart}, ${stateLabel}`;
};

export const formatProductLayerTooltip = (layers, listingStatus, labels = {}) => {
  const items = Array.isArray(layers) ? layers : [];
  if (!items.length) return labels.master || '';

  const names = items.map(layerTitleOf).join(', ');
  if (!listingStatus || !hasChannelProductLayer(items)) return names;

  return `${names}, ${recordStateLabel(listingStatus, labels)}`;
};

export const getDefaultProductLayerIds = () => [MASTER_LAYER_VALUE];

export const buildDefaultProductLayerFilter = (options = [], salesChannels = []) => {
  const layerIds = getDefaultProductLayerIds(options);
  const normalizedOptions = normalizeProductLayerOptions(layerIds, salesChannels);

  return {
    operator: 'oneOf',
    value: restoreProductLayerFilterValue(layerIds, normalizedOptions),
  };
};
